import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router';
import './App.css';
import './styles/detailPages.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import IntroScreen from './components/IntroScreen';
import Journal from './components/Journal';
import PrayerRequests from './components/PrayerRequests';
import Resources from './components/Resources';
import Settings from './components/Settings';
import PrayerGuideGenerator from './components/PrayerGuideGenerator';
import AppLauncher from './components/AppLauncher';
import ScheduleManager from './components/ScheduleManager';
import BibleHub from './components/BibleHub';
import DevotionalHub from './components/DevotionalHub';
import PracticeDashboard from './components/PracticeDashboard';
import VerseDeliveryScheduler from './components/VerseDeliveryScheduler';
import VerseDeliveryConfirmation from './components/VerseDeliveryConfirmation';
import DailyDevotional from './components/DailyDevotional';
import DailyPrayer from './components/DailyPrayer';
import BibleVerseOfTheDay from './components/BibleVerseOfTheDay';
import BibleReadingPlan from './components/BibleReadingPlan';
import ScriptureMemorization from './components/ScriptureMemorization';
import PrayerCalendar from './components/Calendar';
import DeliveryCalendar from './components/DeliveryCalendar';
import SpaceHub, { PracticeMerits } from './components/SpaceHub';
import PrayerTimer from './components/PrayerTimer';
import WorshipVideos from './components/WorshipVideos';
import PrayerGarden from './components/PrayerGarden';
import ScriptureFinder from './components/ScriptureFinder';
import BibleReader from './components/EnhancedBibleReader';
import WorshipLocator from './components/WorshipLocator';
import SignIn from './components/SignIn';
import Premium from './components/Premium';
import AdminDashboard from './components/AdminDashboard';
import ScrollToTop from './components/ScrollToTop';
import DeliveryPreview from './components/DeliveryPreview';
import PrayerLab from './components/PrayerLab';
import VerseStudio from './components/VerseStudio';
import PrimaryNavigation from './components/PrimaryNavigation';
import SharedReader from './components/SharedReader';
import { SYNC_ERROR_EVENT, SYNC_STATUS_EVENT } from './services/userStorage';

// Retain the passage when opening a bookmarked URL from an earlier app version.
function LegacyBibleRedirect() {
  const { bookId } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/bible/reader${bookId ? `/${bookId}` : ''}${search}${hash}`} replace />;
}

function SyncStatusBanner() {
  const [message, setMessage] = useState('');

  useEffect(() => {
    const onError = (event) => setMessage(
      event.detail?.message || 'A recent change has not been saved to your account yet.'
    );
    const onStatus = (event) => {
      if (event.detail?.state === 'synced' && event.detail?.failed === 0) setMessage('');
      if (event.detail?.state === 'error') {
        setMessage((current) => current || 'A recent change has not been saved to your account yet. Keep this page open and try again.');
      }
    };
    window.addEventListener(SYNC_ERROR_EVENT, onError);
    window.addEventListener(SYNC_STATUS_EVENT, onStatus);
    return () => {
      window.removeEventListener(SYNC_ERROR_EVENT, onError);
      window.removeEventListener(SYNC_STATUS_EVENT, onStatus);
    };
  }, []);

  return message ? <div className="app-sync-alert" role="alert">{message}</div> : null;
}

// The private routed tree is keyed by the verified user's immutable id. When
// the account changes, every screen remounts and re-reads only that user's
// hydrated cloud state. Signed-out users never mount this tree.
function AppRoutes() {
  const {
    configured,
    scope,
    loading,
    user,
    profile,
    accessAllowed,
    licenseStatus,
    startupSlow,
    startupIssue,
    hasProtectedUnsyncedChanges,
    fatalError,
    retryStartup,
    discardUnsyncedChanges,
    signOut
  } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="app-splash" role="status" aria-live="polite">
        <div className="app-splash-card">
          <span className="app-splash-mark" aria-hidden="true">✦</span>
          <strong>Opening your quiet place</strong>
          <span className="app-splash-spinner" />
          {startupSlow
            ? <small>Your secure account data is taking longer than usual to load…</small>
            : <small>Restoring your practice…</small>}
        </div>
      </div>
    );
  }

  if (fatalError) {
    return (
      <main className="auth-blocked" role="alert">
        <section className="auth-blocked-card">
          <span aria-hidden="true">✦</span>
          <h1>We couldn’t open your private space safely.</h1>
          <p>{fatalError}</p>
          <div>
            <button onClick={retryStartup}>Try again</button>
            {user ? <button className="is-secondary" onClick={signOut}>Sign out</button> : null}
          </div>
          {hasProtectedUnsyncedChanges ? (
            <button className="auth-discard" type="button" onClick={discardUnsyncedChanges}>
              Discard unsaved changes
            </button>
          ) : null}
        </section>
      </main>
    );
  }

  if (!configured || !user) {
    const requestedPath = location.pathname === '/login'
      ? (location.state?.from || '/')
      : `${location.pathname}${location.search}${location.hash}`;
    return <SignIn returnTo={requestedPath} />;
  }

  if (!accessAllowed) {
    const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || '';
    const statusLabel = licenseStatus === 'missing' ? 'not provisioned' : licenseStatus.replace(/_/g, ' ');
    return (
      <main className="auth-blocked" role="alert">
        <section className="auth-blocked-card">
          <span aria-hidden="true">✦</span>
          <h1>Your Prayer Guide access is not active.</h1>
          <p>
            {profile?.email ? `${profile.email} is signed in, but` : 'Your account is signed in, but'} its
            license is {statusLabel}. No private app data has been opened or changed.
          </p>
          <div>
            <button onClick={retryStartup}>Check again</button>
            <button className="is-secondary" onClick={signOut}>Sign out</button>
          </div>
          {supportEmail ? <a href={`mailto:${supportEmail}`}>Contact support</a> : null}
          {startupIssue ? <p className="auth-notice" role="status">{startupIssue}</p> : null}
          {hasProtectedUnsyncedChanges ? (
            <button className="auth-discard" type="button" onClick={discardUnsyncedChanges}>
              Discard unsaved changes
            </button>
          ) : null}
        </section>
      </main>
    );
  }

  return (
    <>
      <PrimaryNavigation />
      <SyncStatusBanner />
      <Routes key={scope}>
        {/* Daily reading is ready before any optional setup. */}
        <Route path="/" element={<AppLauncher />} />

        {/* Prayer opens today’s reading; creation and delivery remain optional. */}
        <Route path="/prayer" element={<DailyPrayer />} />
        <Route path="/prayer/generate" element={<PrayerGuideGenerator />} />
        <Route path="/prayer/schedule" element={<ScheduleManager />} />
        <Route path="/schedule" element={<VerseDeliveryScheduler />} />
        <Route path="/schedule/confirmed" element={<VerseDeliveryConfirmation />} />
        <Route path="/reader/:contentType" element={<SharedReader />} />

        {/* Door two — Bible: the reader plus every Scripture surface as a tab. */}
        <Route path="/bible" element={<BibleHub />}>
          <Route index element={<Navigate to="/bible/reader" replace />} />
          <Route path="reader" element={<BibleReader />} />
          <Route path="reader/:bookId" element={<BibleReader />} />
          <Route path="verse-of-the-day" element={<BibleVerseOfTheDay />} />
          <Route path="explore" element={<ScriptureFinder />} />
          <Route path="plans" element={<BibleReadingPlan />} />
          <Route path="memorize" element={<ScriptureMemorization />} />
        </Route>

        {/* Door three — Devotionals: today's reading and its guided prayer. */}
        <Route path="/devotionals" element={<DevotionalHub />}>
          <Route index element={<DailyDevotional />} />
          <Route path="prayer" element={<Navigate to="/prayer" replace />} />
        </Route>

        <Route path="/practice" element={<PracticeDashboard />} />
        <Route path="/practice/deliveries" element={<DeliveryCalendar />} />
        <Route path="/practice/history" element={<DeliveryPreview />} />
        <Route path="/practice/journal" element={<Journal />} />
        <Route path="/practice/requests" element={<PrayerRequests />} />
        <Route path="/practice/garden" element={<PrayerGarden />} />
        <Route path="/practice/prayer-calendar" element={<PrayerCalendar />} />
        <Route path="/practice/timer" element={<PrayerTimer />} />
        <Route path="/practice/merits" element={<PracticeMerits />} />

        <Route path="/more" element={<SpaceHub />} />
        <Route path="/more/worship" element={<WorshipVideos />} />
        <Route path="/more/locator" element={<WorshipLocator />} />
        <Route path="/more/resources" element={<Resources />} />
        <Route path="/prayer-lab" element={<PrayerLab />} />
        <Route path="/verse-studio" element={<VerseStudio />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/login" element={<SignIn />} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/intro" element={<IntroScreen />} />

        {/* Every pre-launcher path still resolves to its new home. */}
        <Route path="/read" element={<Navigate to="/bible/reader" replace />} />
        <Route path="/read/bible-reader" element={<LegacyBibleRedirect />} />
        <Route path="/read/bible-reader/:bookId" element={<LegacyBibleRedirect />} />
        <Route path="/bible-reader" element={<LegacyBibleRedirect />} />
        <Route path="/bible-reader/:bookId" element={<LegacyBibleRedirect />} />
        <Route path="/scripture-finder" element={<Navigate to="/bible/explore" replace />} />

        <Route path="/pray-read" element={<Navigate to="/prayer" replace />} />
        <Route path="/pray-read/generator" element={<Navigate to="/prayer/generate" replace />} />
        <Route path="/pray-read/bible-reader" element={<LegacyBibleRedirect />} />
        <Route path="/pray-read/bible-reader/:bookId" element={<LegacyBibleRedirect />} />
        <Route path="/pray-read/scripture" element={<Navigate to="/bible/explore" replace />} />
        <Route path="/pray-read/timer" element={<Navigate to="/practice/timer" replace />} />
        <Route path="/pray-read/requests" element={<Navigate to="/practice/requests" replace />} />
        <Route path="/prayer-guide" element={<Navigate to="/prayer/generate" replace />} />

        <Route path="/daily-dose" element={<Navigate to="/devotionals" replace />} />
        <Route path="/daily-verse" element={<Navigate to="/bible/verse-of-the-day" replace />} />
        <Route path="/daily-devotional" element={<Navigate to="/devotionals" replace />} />
        <Route path="/prayers" element={<Navigate to="/prayer" replace />} />
        <Route path="/daily-prayer" element={<Navigate to="/prayer" replace />} />

        <Route path="/practice/calendar" element={<Navigate to="/practice/deliveries" replace />} />
        <Route path="/calendar" element={<Navigate to="/practice/deliveries" replace />} />
        <Route path="/journal" element={<Navigate to="/practice/journal" replace />} />
        <Route path="/requests" element={<Navigate to="/practice/requests" replace />} />
        <Route path="/garden" element={<Navigate to="/practice/garden" replace />} />
        <Route path="/timer" element={<Navigate to="/practice/timer" replace />} />
        <Route path="/delivery-preview" element={<Navigate to="/practice/history" replace />} />
        <Route path="/resources" element={<Navigate to="/more/resources" replace />} />
        <Route path="/worship" element={<Navigate to="/more/worship" replace />} />
        <Route path="/worship-locator" element={<Navigate to="/more/locator" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <AppRoutes />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
